import { IconTile } from "@design";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cloneElement, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useParams } from "react-router";
import { Page } from "@/components/Page";
import { Text } from "@/components/Text";
import { NotFoundPage } from "@/features/errors/NotFoundPage";
import { HelpMarkdown } from "./HelpMarkdown";
import { HelpList, HelpRow } from "./HelpRow";
import { GROUP_ICON } from "./helpNav";
import { type HelpArticle, useHelpArticles } from "./useHelpArticles";

/** Scroll to `#heading` when there is one, else to the top, whenever the article or anchor changes. */
function useScrollToAnchor(slug: string | undefined) {
  const { hash } = useLocation();
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run on article change even without a hash
  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      const target = hash ? document.getElementById(decodeURIComponent(hash.slice(1))) : null;
      if (target) target.scrollIntoView({ block: "start" });
      else document.getElementById("help-article-top")?.scrollIntoView?.({ block: "start" });
    });
    return () => cancelAnimationFrame(frame);
  }, [slug, hash]);
}

function PrevNext({ prev, next }: Readonly<{ prev?: HelpArticle; next?: HelpArticle }>) {
  const { t } = useTranslation("help");
  if (!prev && !next) return null;
  const linkClass =
    "flex flex-1 items-center gap-2 rounded-lg border border-border-subtle px-4 py-3 transition-colors btn-focus-ring hover:bg-surface-secondary";
  return (
    <nav aria-label={t("article.articleNav")} className="flex flex-col gap-3 sm:flex-row">
      {prev ? (
        <Link to={`/help/${prev.slug}`} className={linkClass}>
          <ChevronLeft size={16} aria-hidden="true" className="shrink-0 text-content-tertiary" />
          <span className="flex flex-col">
            <Text as="span" variant="caption" tone="tertiary">
              {t("article.previous")}
            </Text>
            <Text as="span" weight="semibold">
              {prev.title}
            </Text>
          </span>
        </Link>
      ) : (
        <span className="hidden flex-1 sm:block" />
      )}
      {next && (
        <Link to={`/help/${next.slug}`} className={`${linkClass} justify-end text-right`}>
          <span className="flex flex-col">
            <Text as="span" variant="caption" tone="tertiary">
              {t("article.next")}
            </Text>
            <Text as="span" weight="semibold">
              {next.title}
            </Text>
          </span>
          <ChevronRight size={16} aria-hidden="true" className="shrink-0 text-content-tertiary" />
        </Link>
      )}
    </nav>
  );
}

/** /help/:slug — one article. Unknown or gated slugs get the normal not-found page. */
export function HelpArticlePage() {
  const { t } = useTranslation("help");
  const { slug } = useParams();
  const articles = useHelpArticles();
  useScrollToAnchor(slug);

  const article = articles.find((a) => a.slug === slug);
  if (!article) return <NotFoundPage />;

  const inGroup = articles.filter((a) => a.group === article.group);
  const index = inGroup.indexOf(article);
  const related = article.related.flatMap((s) => articles.find((a) => a.slug === s) ?? []);

  return (
    <div className="flex min-h-full flex-col">
      <span id="help-article-top" />
      <Page
        icon={
          <IconTile size="lg">
            {cloneElement(GROUP_ICON[article.group], { size: 20, "aria-label": t(`groups.${article.group}`) })}
          </IconTile>
        }
        title={article.title}
        subtitle={article.summary}
      >
        <article className="max-w-3xl">
          <HelpMarkdown>{article.body}</HelpMarkdown>
        </article>
        <div className="flex max-w-3xl flex-col gap-8 border-t border-border-subtle pt-8">
          {related.length > 0 && (
            <HelpList title={t("article.related")}>
              {related.map((r) => (
                <HelpRow key={r.slug} to={`/help/${r.slug}`} title={r.title} detail={r.summary} />
              ))}
            </HelpList>
          )}
          <PrevNext prev={inGroup[index - 1]} next={inGroup[index + 1]} />
        </div>
      </Page>
    </div>
  );
}
